import React from 'react';
import { cn } from '@/lib/utils';

declare global {
  // Augment Navigator to include non-standard deviceMemory without using 'any'
  interface Navigator {
    deviceMemory?: number;
  }
}

interface AnimatedAbaskLogoProps {
  className?: string;
  size?: 'mobile' | 'tablet' | 'desktop';
  isAnimated?: boolean;
}

export const AnimatedAbaskLogo: React.FC<AnimatedAbaskLogoProps> = ({
  className,
  size = 'desktop',
  isAnimated = true
}) => {
  const sizeClasses = {
    mobile: 'w-32 h-32 md:w-40 md:h-40',
    tablet: 'w-48 h-48 md:w-56 md:h-56',
    desktop: 'w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 xl:w-[28rem] xl:h-[28rem]'
  };

  // Performance optimization: disable animation on low memory devices
  const shouldAnimate = React.useMemo(() => {
    if (!isAnimated) return false;
    
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return false;
    
    // Check device memory (non-standard property)
    const deviceMemory = window.navigator.deviceMemory;
    if (typeof deviceMemory === 'number' && deviceMemory < 4) return false;
    
    return true;
  }, [isAnimated]);

  return (
    <div className={cn('relative flex items-center justify-center', sizeClasses[size], className)}>
      <svg 
        viewBox="0 0 512 512" 
        preserveAspectRatio="xMidYMid meet"
        className={cn(
          'w-full h-auto',
          shouldAnimate && 'is-animated'
        )}
        style={{
          '--brand-primary': 'rgb(255 255 255 / 4%)',
          '--brand-gold': '#213971',
          '--stroke-color': 'var(--brand-gold)',
          '--stroke-width': '3',
          '--dash': '400',
          '--gap': '600', 
          '--stroke-speed': '12s',
          '--spark-size': '4px',
          '--spark-speed': '10s',
          '--glow-color': 'var(--brand-gold)',
          '--glow-strength': '2'
        } as React.CSSProperties}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="glowFilter" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          <filter id="strongGlow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="6" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* High-Quality ABASK Logo - Direct from Original SVG */}
        <g 
          dangerouslySetInnerHTML={{
            __html: `
              <path 
                id="abask-logo-outline"
                fill="var(--brand-primary)" 
                stroke="${shouldAnimate ? 'var(--stroke-color)' : 'none'}" 
                stroke-width="${shouldAnimate ? 'var(--stroke-width)' : '0'}" 
                filter="${shouldAnimate ? 'url(#glowFilter)' : 'none'}"
                stroke-dasharray="${shouldAnimate ? '400 600' : 'none'}"
                stroke-dashoffset="${shouldAnimate ? '1000' : '0'}"
                class="${shouldAnimate ? 'animate-stroke-sweep' : ''}"
                d="M 312.500 19.746 C 300.400 22.799, 290.374 25.393, 290.220 25.512 C 289.999 25.681, 451.631 343.618, 452.539 344.802 C 452.951 345.340, 506.443 353.891, 506.849 353.484 C 507.487 352.846, 336.584 14.685, 335.495 14.430 C 334.948 14.302, 324.600 16.694, 312.500 19.746 M 242 37.047 C 235.125 38.532, 224.662 40.762, 218.750 42.004 C 212.838 43.245, 208 44.640, 208 45.103 C 208 45.998, 353.764 318.973, 354.927 320.257 C 355.693 321.102, 410.989 330.344, 411.604 329.730 C 412.017 329.316, 256.332 35.906, 255.177 34.924 C 254.805 34.606, 248.875 35.562, 242 37.047 M 93.095 204.508 L 5.784 355.016 19.838 380.240 C 27.568 394.113, 34.254 405.562, 34.696 405.682 C 35.266 405.836, 182.227 154.993, 209.369 107.538 L 211.239 104.270 196.401 79.135 C 188.240 65.311, 181.303 54, 180.984 54 C 180.666 54, 141.116 121.729, 93.095 204.508 M 231.278 135.226 C 230.724 135.927, 191.686 203.100, 144.526 284.500 C 97.366 365.900, 58.180 433.458, 57.446 434.630 C 56.223 436.582, 57.254 438.646, 69.796 459.354 C 80.158 476.462, 83.751 481.650, 84.592 480.724 C 85.203 480.051, 123.872 412.450, 170.523 330.500 C 217.174 248.550, 255.780 180.784, 256.313 179.909 C 257.054 178.693, 254.390 173.150, 245.020 156.409 C 238.275 144.359, 232.651 134.377, 232.521 134.226 C 232.391 134.075, 231.832 134.525, 231.278 135.226 M 248.500 344.622 C 222.866 347.255, 189.415 352.035, 188.557 353.189 C 187.078 355.180, 166.970 395.304, 167.291 395.625 C 167.532 395.865, 173.752 394.959, 181.114 393.611 C 214.199 387.554, 240.472 385.562, 277.500 386.305 C 305.031 386.858, 315.136 387.597, 336 390.584 C 380.744 396.991, 428.518 410.443, 470.851 428.554 C 477.757 431.508, 483.471 433.830, 483.549 433.713 C 483.627 433.596, 487.893 425.285, 493.029 415.245 C 502.025 397.659, 502.297 396.936, 500.434 395.539 C 489.360 387.231, 432.557 368.022, 389.867 358.149 C 348.178 348.507, 319.422 344.920, 280.500 344.508 C 265.100 344.345, 250.700 344.396, 248.500 344.622 M 222 410.127 C 207.782 411.083, 182.058 414.288, 168.003 416.855 L 158.505 418.589 147.820 439.045 C 141.942 450.295, 137.104 460.093, 137.067 460.817 C 137.025 461.643, 137.675 461.920, 138.809 461.561 C 143.536 460.060, 177.325 454.936, 192.500 453.418 C 212.849 451.382, 258.219 451.112, 277 452.915 C 337.752 458.746, 385.834 471.258, 449.261 497.742 C 451.329 498.605, 453.168 499.129, 453.347 498.906 C 454.032 498.053, 472 463.082, 472 462.603 C 472 461.324, 458.274 454.644, 446.500 450.192 C 385.578 427.158, 323.426 413.379, 265.189 409.997 C 246.397 408.906, 239.879 408.926, 222 410.127"
              />"
            `
          }}
        />
        
        {/* Sparks Group - Only if animation enabled */}
        {shouldAnimate && (
          <g id="sparks">
<circle r={4} fill="var(--stroke-color)" opacity="0.9">
  <animateMotion dur="var(--spark-speed)" repeatCount="indefinite" rotate="auto">
    <mpath href="#abask-logo-outline" />
  </animateMotion>
</circle>

<circle r={4} fill="var(--stroke-color)" opacity="0.7">
  <animateMotion
    dur="var(--spark-speed)"
    repeatCount="indefinite"
    rotate="auto"
    begin="3s"
  >
    <mpath href="#abask-logo-outline" />
  </animateMotion>
</circle>

<circle r={4} fill="var(--stroke-color)" opacity="0.8">
  <animateMotion
    dur="var(--spark-speed)"
    repeatCount="indefinite"
    rotate="auto"
    begin="6s"
  >
    <mpath href="#abask-logo-outline" />
  </animateMotion>
</circle>

          </g>
        )}
      </svg>
      
      {/* CSS Animations via global styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .is-animated .animate-stroke-sweep {
            animation: strokeSweep var(--stroke-speed) infinite linear;
          }
          
          @keyframes strokeSweep {
            0% { stroke-dashoffset: 1000; }
            100% { stroke-dashoffset: 0; }
          }
          
          @media (prefers-reduced-motion: reduce) {
            .is-animated * {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
            }
          }
        `
      }} />
    </div>
  );
};

export default AnimatedAbaskLogo;